import { isSystemTag } from '@tunasong/models'
import type { RenderElementPropsExt } from '@tunasong/plugin-lib'
import { useEditor } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import { useDeviceUISettings, useMergeRefs } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { CSSProperties, FC, RefObject } from 'react'
import type { DragElementWrapper, DragSourceOptions } from 'react-dnd'
import { useSelected } from 'slate-react'
import { BlockDecorationsBar } from './block-decorations-bar.js'
import { BlockGutter } from './block-gutter.js'
import styles from './core-block.module.css'
import { HiddenBlock } from './hidden-block.js'

export interface ConfigureBlock {
  prop: string
  validate: (val: unknown) => boolean
  type?: 'text'

  title?: string
}

/** DOM event handlers that block consumers may need to attach */
type BlockEventHandlers = Pick<
  React.DOMAttributes<HTMLElement>,
  'onClick' | 'onDoubleClick' | 'onMouseDown' | 'onMouseUp' | 'onContextMenu' | 'onKeyDown' | 'onKeyUp'
>

export interface CoreBlockProps extends RenderElementPropsExt, BlockEventHandlers {
  className?: string
  title?: string
  style?: CSSProperties
  component?: 'div' | 'span'
  element: CoreElement
  focus?: boolean
  /** Right border on focus */
  border?: boolean | 'right'
  menu?: boolean

  label?: string

  /** Internal */
  dragRef?: DragElementWrapper<DragSourceOptions>
  blockRef?: RefObject<HTMLDivElement | null>
  dropLine?: 'bottom' | 'left' | 'right' | 'top' | ''
  isDragOver?: boolean
}

/**
 * Single wrapper for blocks that need selection awareness (focus or menu).
 * Calls useSelected() once and provides both focus styling and gutter hasFocus.
 */
const SelectionAwareBlock: FC<{
  focus: boolean
  menu: boolean
  border: boolean | 'right'
  isDragOver?: boolean
  isInline: boolean
  editor: ReturnType<typeof useEditor>
  element: CoreElement
  label?: string
  dragRef?: DragElementWrapper<DragSourceOptions>
  children: (blockClassName: string, gutter: React.ReactNode) => React.ReactNode
}> = ({ focus, menu, border, isInline, editor, element, label, dragRef, children }) => {
  const blockSelected = useSelected()
  const hasFocus = focus && blockSelected
  const hasBorder = border === true
  const hasFocusBorder = hasFocus && border === true
  const hasFocusRightBorder = hasFocus && border === 'right'

  const blockClassName = cn(styles.block, {
    [styles['block--inline']]: isInline,
    [styles['block--border']]: hasBorder,
    [styles['block--focused']]: hasFocusBorder,
    [styles['block--focusRight']]: hasFocusRightBorder,
  })

  const gutter = menu ? (
    <BlockGutter
      editor={editor}
      element={element}
      blockName={`${element.type[0].toUpperCase()}${element.type.slice(1)} Block`}
      hasFocus={hasFocus}
      label={label}
      dragRef={dragRef}
    />
  ) : null

  return <>{children(blockClassName, gutter)}</>
}

/** A Block rendered as a plain element with CSS variables for theme values */
export const CoreBlock: FC<CoreBlockProps> = props => {
  const {
    className,
    style,
    component: Component = 'div',
    focus = true,
    menu = false,
    border = 'right',
    attributes,
    element,
    label,
    children,
    /** DnD props */
    blockRef,
    dragRef,
    dropLine,
    isDragOver,
    /** DOM event handlers */
    onClick,
    onDoubleClick,
    onMouseDown,
    onMouseUp,
    onContextMenu,
    onKeyDown,
    onKeyUp,
  } = props

  const editor = useEditor()

  const tagsFilter = useDeviceUISettings(state => state.tagsFilter)

  const multiRootRef = useMergeRefs(attributes.ref, blockRef)

  const isInline = Component === 'span'
  const needsSelection = focus || menu

  // Hide all tagged blocks (non-system tags) not matching filter if filter is set
  const hiddenByTagsFilter =
    tagsFilter.length > 0 && element.tags?.some(tag => !isSystemTag(tag) && !tagsFilter.includes(tag)) === true

  if (hiddenByTagsFilter) {
    return (
      <HiddenBlock contentEditable={false} text="Hidden by tags filter" attributes={attributes}>
        {children}
      </HiddenBlock>
    )
  }

  const InnerTag = isInline ? 'span' : 'div'

  const renderBlock = (blockClassName: string, gutter?: React.ReactNode) => (
    <Component {...attributes} ref={multiRootRef} className={styles.wrapper}>
      {dropLine === 'top' && <div className={styles.dropLine} contentEditable={false} />}
      <InnerTag
        className={cn(blockClassName, className)}
        style={style}
        title={props.title}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onContextMenu={onContextMenu}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      >
        {gutter}
        <BlockDecorationsBar editor={editor} element={element} />
        <>{children}</>
      </InnerTag>
      {dropLine === 'bottom' && <div className={styles.dropLine} contentEditable={false} />}
    </Component>
  )

  if (needsSelection) {
    return (
      <SelectionAwareBlock
        focus={focus}
        menu={menu}
        border={border}
        isDragOver={isDragOver}
        isInline={isInline}
        editor={editor}
        element={element}
        label={label}
        dragRef={dragRef}
      >
        {renderBlock}
      </SelectionAwareBlock>
    )
  }

  const hasBorder = border === true
  const simpleClassName = cn(styles.block, {
    [styles['block--inline']]: isInline,
    [styles['block--border']]: hasBorder,
  })

  return renderBlock(simpleClassName)
}
